/**
 * An empty list's one look (FEED-002, ANTI-013; TMMRSL-0073), in two kinds the person can tell apart:
 * `none` — nothing exists yet, with the screen's primary action where it has one ("Invite a
 * subscriber"); `noMatch` — things exist but the filters hide them all, with **Clear filters**, which
 * resets the screen's `useUrlState` keys. A screen with its own wording for the action passes it as
 * `action` instead.
 */
import type { ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/theme';

export interface EmptyStateProps {
  kind: 'none' | 'noMatch';
  title: string;
  body?: string | undefined;
  /** `none`: the screen's primary action; `noMatch`: replaces the Clear filters button. */
  action?: ReactNode;
  /** `noMatch`: resets the screen's filters; shown as Clear filters when no `action` is given. */
  onClear?: (() => void) | undefined;
}

export function EmptyState({ kind, title, body, action, onClear }: EmptyStateProps) {
  const { t } = useTranslation();
  const shownAction =
    action ??
    (kind === 'noMatch' && onClear ? (
      <Button onClick={onClear}>{t('common.clearFilters')}</Button>
    ) : null);
  return (
    <Box
      data-empty={kind}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 3, px: 0.5 }}
    >
      <Icon
        name={kind === 'noMatch' ? 'search_off' : 'inbox'}
        size="lg"
        sx={{ color: 'text.secondary', mt: 0.25 }}
      />
      <Box
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Typography>{title}</Typography>
        {body ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {body}
          </Typography>
        ) : null}
        {shownAction}
      </Box>
    </Box>
  );
}
